import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChuziApiError,
  insufficientCreditsPool,
  isInsufficientCreditsError,
} from "@chuzi/shared/api";
import { t, CHUZI_AI_GENERATOR_VIBES, CHUZI_AI_VIBE_LABELS, AUDIENCE_AGE_OPTIONS } from "@chuzi/shared/config";
import type {
  AiSceneGenerationResult,
  AiSceneMode,
  AiStoryTokenEstimate,
  AudienceAge,
  RealmId,
  SceneListItem,
  UserProfile,
} from "@chuzi/shared/types";
import { api } from "../lib/api";
import {
  applyChuziAiSceneCreate,
  applyChuziAiSceneOverwrite,
} from "../lib/chuzi-ai-scene-builder";
import {
  composeSceneBrief,
  loadGeneratorPrefs,
  persistGeneratorPrefs,
} from "../lib/story-generator-context";
import { useSpendingModal } from "../lib/spending-modal";
import { AddCreditsButton } from "./add-credits-button";
import { SpeechTextarea } from "./speech-textarea";

interface ChuziAiWizardProps {
  realm: RealmId;
  storyId: string;
  scenes: SceneListItem[];
  mode: AiSceneMode;
  targetScene?: SceneListItem | null;
  insertAfterSceneId?: string | null;
  user?: UserProfile | null;
  onApplied: (sceneId: string) => void;
  onCancel: () => void;
}

type WizardStep = "vibes" | "audience" | "details" | "generating" | "applying";

export function ChuziAiWizard({
  realm,
  storyId,
  scenes,
  mode,
  targetScene,
  insertAfterSceneId,
  user = null,
  onApplied,
  onCancel,
}: ChuziAiWizardProps) {
  const { openSpendingModal } = useSpendingModal();

  const savedPrefs = useMemo(() => loadGeneratorPrefs(), []);
  const [selectedVibes, setSelectedVibes] = useState<Set<string>>(
    () => new Set(savedPrefs?.vibes ?? []),
  );
  const [audienceAge, setAudienceAge] = useState<AudienceAge | "">(
    savedPrefs?.audience_age ?? "",
  );
  const [brief, setBrief] = useState("");
  const [estimate, setEstimate] = useState<AiStoryTokenEstimate | null>(null);
  const [sceneCredits, setSceneCredits] = useState(mode === "create" ? 1 : 0);
  const [canAfford, setCanAfford] = useState(true);
  const [totalCreditsNeeded, setTotalCreditsNeeded] = useState(1);
  const [generation, setGeneration] = useState<AiSceneGenerationResult | null>(null);
  const [step, setStep] = useState<WizardStep>("vibes");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isWilds = realm === "wilds";
  const accent = isWilds ? "#78c878" : "#7eb8ff";
  const accentAlpha = isWilds ? "rgba(120,200,120," : "rgba(126,184,255,";
  const accentBg = isWilds ? "rgba(90,180,90,0.15)" : "rgba(126,184,255,0.15)";
  const border = isWilds ? "rgba(90,180,90,0.35)" : "rgba(126,184,255,0.35)";
  const panelBg = isWilds ? "rgba(20,40,20,0.95)" : "rgba(10,16,32,0.95)";


  function composeFull(): string {
    return composeSceneBrief(realm, Array.from(selectedVibes), audienceAge, brief);
  }

  const refreshEstimate = useCallback(async () => {
    const full = composeSceneBrief(realm, Array.from(selectedVibes), audienceAge, brief);
    if (full.trim().length < 8) {
      setEstimate(null);
      setCanAfford(true);
      return;
    }
    try {
      const res = await api.ai.sceneEstimate({
        story_id: storyId,
        brief: full,
        mode,
        scene_id: mode === "overwrite" ? targetScene?.id : undefined,
      });
      setEstimate(res.estimate);
      setSceneCredits(res.scene_credits);
      setTotalCreditsNeeded(res.total_credits);
      setCanAfford(!!user?.is_admin || res.can_afford);
    } catch {
      setEstimate(null);
      setCanAfford(true);
    }
  }, [brief, selectedVibes, audienceAge, realm, storyId, mode, targetScene?.id, user?.is_admin]);

  useEffect(() => {
    if (step !== "details") return;
    const timer = setTimeout(() => refreshEstimate(), 400);
    return () => clearTimeout(timer);
  }, [step, refreshEstimate]);

  function handleInsufficient(err: unknown): boolean {
    if (err instanceof ChuziApiError && isInsufficientCreditsError(err)) {
      const pool = insufficientCreditsPool(err) ?? "create";
      const body = err.body as { credits_needed?: number } | null;
      openSpendingModal({
        pool,
        creditsNeeded: body?.credits_needed,
        onSuccess: () => refreshEstimate(),
      });
      return true;
    }
    return false;
  }

  function toggleVibe(vibe: string) {
    setSelectedVibes((prev) => {
      const next = new Set(prev);
      if (next.has(vibe)) next.delete(vibe);
      else next.add(vibe);
      return next;
    });
  }

  async function handleGenerate() {
    const full = composeFull();
    if (full.trim().length < 8) return;
    setBusy(true);
    setError(null);
    setStep("generating");
    try {
      const res = await api.ai.sceneGenerate({
        story_id: storyId,
        brief: full,
        mode,
        scene_id: mode === "overwrite" ? targetScene?.id : undefined,
      });
      setSceneCredits(res.generation.scene_credits);
      persistGeneratorPrefs({
        vibes: Array.from(selectedVibes),
        audience_age: audienceAge || undefined,
      });

      setStep("applying");
      try {
        let sceneId: string;
        if (mode === "overwrite" && targetScene) {
          const updated = await applyChuziAiSceneOverwrite(targetScene.id, res.generation.scene);
          sceneId = updated.id;
        } else {
          const created = await applyChuziAiSceneCreate(
            storyId,
            res.generation.scene,
            scenes,
            insertAfterSceneId,
          );
          sceneId = created.id;
        }
        onApplied(sceneId);
        return;
      } catch (applyErr) {
        if (!handleInsufficient(applyErr)) {
          setError(applyErr instanceof Error ? applyErr.message : t(realm, "chuzi_ai_build_failed"));
        }
        setGeneration(res.generation);
        setStep("details");
        return;
      }
    } catch (err) {
      if (!handleInsufficient(err)) {
        const msg =
          err instanceof ChuziApiError
            ? (err.body as { error?: string })?.error ?? err.message
            : err instanceof Error
              ? err.message
              : t(realm, "chuzi_ai_generate_failed");
        setError(msg);
      }
      setStep("details");
    } finally {
      setBusy(false);
    }
  }

  const aiCredits = generation?.credits_charged ?? estimate?.credits_needed ?? 0;
  const totalCredits = aiCredits + sceneCredits;
  const canGenerate = composeFull().trim().length >= 8 && !busy;

  const isGenerating = step === "generating" || step === "applying";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        style={{
          width: "min(560px, calc(100vw - 48px))",
          maxHeight: "calc(100vh - 80px)",
          background: panelBg,
          border: `1px solid ${border}`,
          borderRadius: 16,
          backdropFilter: "blur(12px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "editorFadeIn 0.3s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px 12px",
            borderBottom: `1px solid ${accentAlpha}0.15)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: accent, fontSize: 13, fontWeight: 700 }}>
              {t(realm, "chuzi_ai_name")}
            </span>
            <span style={{ color: "rgba(232,240,255,0.5)", fontSize: 11 }}>&middot;</span>
            <span style={{ color: "rgba(232,240,255,0.7)", fontSize: 12 }}>
              {mode === "overwrite"
                ? t(realm, "chuzi_ai_scene_overwrite")
                : t(realm, "chuzi_ai_scene_create")}
            </span>
          </div>
          {/* Step indicator */}
          {!isGenerating ? (
            <div style={{ display: "flex", gap: 4 }}>
              {(["vibes", "audience", "details"] as const).map((s, i) => (
                <div
                  key={s}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: step === s ? accent : `${accentAlpha}0.25)`,
                    transition: "background 200ms",
                  }}
                />
              ))}
            </div>
          ) : null}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: "20px 20px 16px" }}>
          {step === "vibes" && <VibesStep
            vibes={selectedVibes}
            onToggle={toggleVibe}
            accent={accent}
            accentAlpha={accentAlpha}
            border={border}
            realm={realm}
          />}

          {step === "audience" && <AudienceStep
            audienceAge={audienceAge}
            onSelect={setAudienceAge}
            accent={accent}
            accentAlpha={accentAlpha}
            border={border}
            realm={realm}
          />}

          {step === "details" && <DetailsStep
            brief={brief}
            onBriefChange={setBrief}
            accent={accent}
            accentAlpha={accentAlpha}
            border={border}
            realm={realm}
            error={error}
          />}

          {isGenerating && <GeneratingStep
            step={step}
            accent={accent}
            accentAlpha={accentAlpha}
            realm={realm}
          />}
        </div>

        {/* Footer */}
        {!isGenerating ? (
          <div
            style={{
              padding: "12px 20px 16px",
              borderTop: `1px solid ${accentAlpha}0.15)`,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {step === "details" && estimate ? (
              <div style={{ fontSize: 11, color: "rgba(232,240,255,0.5)", textAlign: "center" }}>
                ~{totalCredits} ai credits{sceneCredits > 0 ? ` + ${sceneCredits} scene credits` : ""}
              </div>
            ) : null}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => {
                  if (step === "vibes") onCancel();
                  else if (step === "audience") setStep("vibes");
                  else if (step === "details") setStep("audience");
                }}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "rgba(232,240,255,0.6)",
                  background: "transparent",
                  border: `1px solid ${accentAlpha}0.25)`,
                  borderRadius: 8,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {step === "vibes" ? t(realm, "editor_cancel") : t(realm, "wizard_back", "Back")}
              </button>

              {step === "details" ? (
                <AddCreditsButton
                  realm={realm}
                  pool="create"
                  label={t(realm, "chuzi_ai_generate")}
                  canAfford={canAfford}
                  creditsNeeded={totalCreditsNeeded}
                  user={user}
                  onAction={handleGenerate}
                  disabled={!canGenerate}
                  style={{
                    flex: 1,
                    padding: "10px 0",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#fff",
                    background: `linear-gradient(135deg, ${accentAlpha}0.4), ${accentAlpha}0.2))`,
                    border: `1px solid ${accent}`,
                    borderRadius: 8,
                    cursor: canGenerate ? "pointer" : "not-allowed",
                    fontFamily: "inherit",
                    boxShadow: `0 0 16px ${accentAlpha}0.3), 0 0 32px ${accentAlpha}0.15)`,
                    animation: canGenerate ? "accretionBreathe 2.4s ease-in-out infinite" : "none",
                    opacity: canGenerate ? 1 : 0.5,
                  }}
                >
                  {t(realm, "chuzi_ai_generate")}
                </AddCreditsButton>
              ) : (
                <button
                  onClick={() => {
                    if (step === "vibes") setStep("audience");
                    else if (step === "audience") setStep("details");
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 0",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#fff",
                    background: `linear-gradient(135deg, ${accentAlpha}0.35), ${accentAlpha}0.15))`,
                    border: `1px solid ${accent}`,
                    borderRadius: 8,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    boxShadow: `0 0 12px ${accentAlpha}0.25)`,
                  }}
                >
                  {t(realm, "wizard_next", "Next")}
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <style>{`
        @keyframes accretionBreathe {
          0%, 100% { box-shadow: 0 0 12px ${accentAlpha}0.25), 0 0 24px ${accentAlpha}0.12); }
          50% { box-shadow: 0 0 20px ${accentAlpha}0.4), 0 0 40px ${accentAlpha}0.2); }
        }
      `}</style>
    </div>
  );
}

// --- Step subcomponents ---

function VibesStep({ vibes, onToggle, accent, accentAlpha, border, realm }: {
  vibes: Set<string>;
  onToggle: (v: string) => void;
  accent: string;
  accentAlpha: string;
  border: string;
  realm: RealmId;
}) {
  return (
    <div>
      <h3 style={{ color: "#e8f0ff", fontSize: 16, fontWeight: 600, margin: "0 0 6px" }}>
        {t(realm, "generator_vibes_prompt", "Choose your vibes")}
      </h3>
      <p style={{ color: "rgba(232,240,255,0.5)", fontSize: 12, margin: "0 0 16px" }}>
        {t(realm, "wizard_vibes_hint", "Pick the genres and moods for your scene. You can select multiple.")}
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {CHUZI_AI_GENERATOR_VIBES.map((vibe) => {
          const selected = vibes.has(vibe);
          return (
            <button
              key={vibe}
              type="button"
              onClick={() => onToggle(vibe)}
              style={{
                padding: "7px 14px",
                fontSize: 12,
                fontWeight: 500,
                borderRadius: 999,
                border: `1px solid ${selected ? accent : border}`,
                background: selected ? `${accentAlpha}0.2)` : "transparent",
                color: selected ? "#fff" : "rgba(232,240,255,0.7)",
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 150ms ease",
                boxShadow: selected ? `0 0 8px ${accentAlpha}0.3)` : "none",
              }}
            >
              {CHUZI_AI_VIBE_LABELS[vibe]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AudienceStep({ audienceAge, onSelect, accent, accentAlpha, border, realm }: {
  audienceAge: AudienceAge | "";
  onSelect: (age: AudienceAge | "") => void;
  accent: string;
  accentAlpha: string;
  border: string;
  realm: RealmId;
}) {
  return (
    <div>
      <h3 style={{ color: "#e8f0ff", fontSize: 16, fontWeight: 600, margin: "0 0 6px" }}>
        {t(realm, "generator_audience_prompt", "Who's your audience?")}
      </h3>
      <p style={{ color: "rgba(232,240,255,0.5)", fontSize: 12, margin: "0 0 16px" }}>
        {t(realm, "wizard_audience_hint", "This controls vocabulary, sentence length, and themes.")}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {AUDIENCE_AGE_OPTIONS.map((option) => {
          const selected = audienceAge === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onSelect(selected ? "" : option.id)}
              style={{
                padding: "14px 18px",
                borderRadius: 12,
                border: `1px solid ${selected ? accent : border}`,
                background: selected ? `${accentAlpha}0.15)` : "rgba(255,255,255,0.02)",
                color: "#e8f0ff",
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "left",
                transition: "all 150ms ease",
                boxShadow: selected ? `0 0 12px ${accentAlpha}0.2), inset 0 0 8px ${accentAlpha}0.08)` : "none",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>
                {option.label}
              </div>
              <div style={{ fontSize: 12, color: "rgba(232,240,255,0.55)", lineHeight: 1.4 }}>
                {option.description}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DetailsStep({ brief, onBriefChange, accent, accentAlpha, border, realm, error }: {
  brief: string;
  onBriefChange: (v: string) => void;
  accent: string;
  accentAlpha: string;
  border: string;
  realm: RealmId;
  error: string | null;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 260 }}>
      <h3 style={{ color: "#e8f0ff", fontSize: 16, fontWeight: 600, margin: "0 0 6px" }}>
        {t(realm, "chuzi_ai_scene_brief", "Scene direction")}
      </h3>
      <p style={{ color: "rgba(232,240,255,0.5)", fontSize: 12, margin: "0 0 12px" }}>
        {t(realm, "chuzi_ai_scene_brief_hint", "Describe what should happen in this scene. Chuzi AI reads your existing story for characters, tone, and style.")}
      </p>
      <SpeechTextarea
        value={brief}
        onChange={onBriefChange}
        maxLength={2000}
        placeholder={t(realm, "wizard_details_placeholder", "What happens next?")}
        autoFocus
        accent={accent}
        accentBg={`${accentAlpha}0.1)`}
        border={border}
        background={`${accentAlpha}0.06)`}
        minHeight={180}
        resize="none"
        style={{ height: "100%", borderRadius: 10, padding: "14px 16px 48px", fontSize: 14, lineHeight: 1.6 }}
      />
      {error ? (
        <div style={{
          marginTop: 8,
          fontSize: 11,
          color: "#ff9aa2",
          background: "rgba(255,154,162,0.08)",
          border: "1px solid rgba(255,154,162,0.4)",
          borderRadius: 6,
          padding: "6px 10px",
        }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}

function GeneratingStep({ step, accent, accentAlpha, realm }: {
  step: WizardStep;
  accent: string;
  accentAlpha: string;
  realm: RealmId;
}) {
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 200,
      gap: 16,
    }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          border: `3px solid ${accentAlpha}0.3)`,
          borderTopColor: accent,
          animation: "chuzi-spin 0.8s linear infinite",
        }}
      />
      <div style={{ color: "rgba(232,240,255,0.7)", fontSize: 14 }}>
        {step === "generating"
          ? t(realm, "chuzi_ai_generating")
          : t(realm, "chuzi_ai_applying")}
      </div>
    </div>
  );
}
